import { Box, SxProps, Theme } from '@mui/material';
import { ComponentType, ReactNode, SVGProps } from 'react';

import { Card } from '@/components/layout/Card/Card';
import { COLORS } from '@/styles/tokens/colors';

export type DiagramDisplayCardVariant = 'default';

export interface DiagramDisplayCardProps<Value = string> {
  ariaLabel?: string;
  bodySx?: SxProps<Theme>;
  children: ReactNode;
  disabled?: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  onClick: (value: Value) => void;
  // Omit for action cards. Set a boolean for selection cards.
  selected?: boolean;
  sx?: SxProps<Theme>;
  value: Value;
  variant?: DiagramDisplayCardVariant;
}

export function DiagramDisplayCard<Value = string>({
  ariaLabel,
  bodySx,
  children,
  disabled = false,
  icon: Icon,
  onClick,
  selected,
  sx,
  value,
  variant = 'default',
}: DiagramDisplayCardProps<Value>) {
  return (
    <Card
      variant="outlined"
      component="button"
      type="button"
      aria-label={ariaLabel}
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onClick(value)}
      sx={{
        p: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        textAlign: 'left',
        bgcolor: COLORS.PRIMITIVES.WHITE,
        borderRadius: '4px',
        borderColor: selected ? COLORS.GRAY[500] : COLORS.GRAY[200],
        cursor: 'pointer',
        '&:hover:not(:disabled)': {
          borderColor: selected ? COLORS.GRAY[800] : COLORS.GRAY[300],
          boxShadow: (theme) => theme.palette.shadows.xs,
        },
        '&:focus-visible': {
          outline: `2px solid ${COLORS.BLUE[500]}`,
          outlineOffset: 2,
        },
        '&:disabled': {
          cursor: 'not-allowed',
          opacity: 0.5,
        },
        ...sx,
      }}
    >
      {Icon && (
        <Box
          height={94}
          flexShrink={0}
          overflow="hidden"
          bgcolor={variant === 'default' ? COLORS.GRAY[50] : undefined}
        >
          <Icon
            width="100%"
            height="100%"
            style={{ display: 'block' }}
            aria-hidden
          />
        </Box>
      )}
      <Box p={1.5} sx={bodySx}>
        {children}
      </Box>
    </Card>
  );
}
